import { has } from '@/commonFunctions/utils'
import { PlayerInfoSection } from '@/components/common'
import { Close, People, Person } from '@/components/icons'
import { ConfirmModal } from '@/components/modal'
import { LockedRating } from '@/components/profile-limit'
import { CLUB_MEMBERS_DELETE } from '@/constants/Common'
import { ROUTE_PLAYER_PROFILE } from '@/constants/Routes'
import { useRatingLock } from '@/hooks/useRatingLock'
import { useAppSelector } from '@/hooks/useRedux'
import { useRemoveMember } from '@/services'
import { ClubMember } from '@/types'
import { ReactNode, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { clubSelector } from '../selectors'

/** The member's display ratings, preferring provisional values when unrated. */
function memberDisplayRatings(member: ClubMember) {
  const doubles =
    member?.singles === 'NR' && member?.doublesProvisionalRating ? member?.doublesProvisionalRating : member?.doubles
  const singles =
    member?.singles === 'NR' && member?.singlesProvisionalRating ? member?.singlesProvisionalRating : member?.singles
  return { doubles, singles }
}

type PillProps = {
  icon: ReactNode
  locked: boolean
  value: ReactNode
  widthClass: string
}

const MemberRatingPill = ({ icon, locked, value, widthClass }: PillProps) => {
  return (
    <div
      className={`bg-navy-900 bg-opacity-5 rounded-lg px-2 ${widthClass} h-9  flex flex-col justify-center items-center`}
    >
      {icon}
      <span className="text-xs text-navy-900 font-medium">{locked ? <LockedRating /> : value}</span>
    </div>
  )
}

type RemoveConfirmProps = {
  open: boolean
  onClose: () => void
  memberId: number
  callbackFetch?: () => void
}

/** Confirmation modal that performs the member removal. */
const RemoveMemberConfirm = ({ open, onClose, memberId, callbackFetch }: RemoveConfirmProps) => {
  const { t } = useTranslation()
  const { clubId } = useParams()
  const { mutate: removeMember } = useRemoveMember()

  const onRemoveMember = () => {
    removeMember(
      { clubId: Number(clubId), userIds: [memberId] },
      {
        onSuccess: () => {
          onClose()
          toast.success('Removed')
          if (callbackFetch) callbackFetch()
        },
      }
    )
    onClose()
  }

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      title={t('club.removeMember')}
      desc={t('club.removeMemberDesc')}
      onSubmit={onRemoveMember}
    />
  )
}

type RemoveProps = {
  member: ClubMember
  callbackFetch?: () => void
}

/** Remove-member button plus its confirmation modal (delete-permission holders only). */
const RemoveMemberAction = ({ member, callbackFetch }: RemoveProps) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      <button
        className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20"
        onClick={() => setIsOpen(true)}
      >
        <Close className="text-navy-900" />
      </button>
      <RemoveMemberConfirm
        open={isOpen}
        onClose={() => setIsOpen(false)}
        memberId={member.id}
        callbackFetch={callbackFetch}
      />
    </>
  )
}

type Props = {
  member: ClubMember
  callbackFetch?: () => void
}

/** Both rating pills for a member row, locked together by the viewer's quota. */
const MemberRatings = ({ member }: { member: ClubMember }) => {
  const isRatingLocked = useRatingLock()(member?.id)
  const { doubles, singles } = memberDisplayRatings(member)
  return (
    <>
      <MemberRatingPill
        icon={<People className="text-navy-900" />}
        locked={isRatingLocked}
        value={doubles}
        widthClass="w-11"
      />
      <MemberRatingPill
        icon={<Person className="text-navy-900" />}
        locked={isRatingLocked}
        value={singles}
        widthClass="w-10"
      />
    </>
  )
}

export const ClubMemberItem = ({ member, callbackFetch }: Props) => {
  const navigate = useNavigate()

  const { permissions } = useAppSelector(clubSelector)

  return (
    <div className="flex justify-between bg-white rounded-2xl p-4">
      <PlayerInfoSection
        player={member}
        className="cursor-pointer"
        onClick={() => navigate(`${ROUTE_PLAYER_PROFILE}/${member?.id}`)}
      />
      <div className="flex gap-x-1 items-center">
        <MemberRatings member={member} />
        {has(permissions, CLUB_MEMBERS_DELETE) && <RemoveMemberAction member={member} callbackFetch={callbackFetch} />}
      </div>
    </div>
  )
}
